import { Button } from "@/components/ui/button";
import { cdnUrl } from "commons/cdn";
import {
  ChevronDownIcon,
  ClipboardIcon,
  HeartIcon,
  ChatBubbleIcon,
} from "@radix-ui/react-icons";
import React, { useMemo, useState } from "react";
import MyLink from "components/ui/MyLink";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import useSWR from "swr";
import { delFetcher, putFetcher } from "api/swr";
import { useContetLang } from "hooks/config";
import useSWRMutation from "swr/mutation";
import DisplayUpdateTime from "ui/DisplayUpdateTime";
import { useCardAtom } from "./DisplayData";
import { toast } from "@/components/ui/use-toast";
import { MyImage } from "components/MyImage";

const clickableTextClassName =
  "cursor-pointer font-medium underline underline-offset-4";

const StateLabel = ({ state }) => {
  const label = state === 401 ? "public" : "local";
  return (
    <span className="w-fit rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
      {label}
    </span>
  );
};

const OcItem = ({ data, mutate, onAuthorClick }) => {
  const showInfo = useCardAtom((state) => state.info);
  const showbadge = useCardAtom((state) => state.badge);
  const showHeader = useCardAtom((state) => state.header);
  const showReportCount = useCardAtom((state) => state.reportCount);

  if (!data) {
    return null;
  }

  return (
    <div
      className="flex flex-col gap-1 border p-1"
      style={{ borderColor: data.state === 0 ? "pink" : "" }}
    >
      {showHeader && (
        <div className="text-sm">
          <h1 className="">
            <HeaderText data={data} />
          </h1>
          <p className="text-xs text-muted-foreground">
            <DisplayUpdateTime updateTime={data.updateTime} />
          </p>
        </div>
      )}
      <div className="relative inline-block overflow-hidden">
        <div className="absolute left-1 top-1 z-10">
          <StateLabel state={data.state} />
        </div>
        <MyImage
          alt="avatar"
          src={cdnUrl(data.oc_basic_avatar)}
          className="h-auto w-auto object-cover transition-all hover:scale-105 aspect-square"
        />
      </div>
      {showInfo && (
        <AuthorRow data={data} onAuthorClick={onAuthorClick} />
      )}
      {showbadge && <BadgeRow data={data} />}
      {showInfo && <OcInfo data={data} />}
      {showReportCount && <ReportCount data={data} />}
      <Actions data={data} mutate={mutate} />
    </div>
  );
};

const AuthorRow = ({ data, onAuthorClick }) => {
  const clickable = Boolean(onAuthorClick && data?.authorId);

  return (
    <div
      className={`flex items-center gap-2 ${clickable ? "cursor-pointer" : ""}`}
      onClick={
        clickable ? () => onAuthorClick(data.authorId) : undefined
      }
    >
      <img
        src={cdnUrl(data.authorAvatar)}
        alt="avatar"
        className="h-6 w-6 rounded-full object-cover"
      />
      <span
        className={`${
          clickable ? clickableTextClassName : ""
        } truncate max-w-[120px]`}
        title={data.authorName}
      >
        {data.authorName}
      </span>
    </div>
  );
};

const OcInfo = ({ data }) => {
  const [open, setOpen] = useState(false);
  const { data: detail, isLoading } = useSWR(
    open && data?.id ? ["/ocs", { id: data.id }] : null,
  );

  const ocInfo = useMemo(() => {
    const source = Array.isArray(detail) ? detail[0] : detail;
    if (!source) return null;
    const result = {};
    for (const key in source) {
      if (key.startsWith("oc_")) {
        result[key] = source[key];
      }
    }
    return result;
  }, [detail]);

  return (
    <div className="flex flex-col space-y-1">
      <div className="flex items-center gap-2 text-sm">
        <span className="truncate" title={data.oc_basic_name || undefined}>
          name: {data.oc_basic_name || "null"}
        </span>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <span
              className={`${clickableTextClassName} shrink-0`}
              onClick={() => setOpen(true)}
            >
              more
            </span>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>OC JSON</DialogTitle>
            </DialogHeader>
            {isLoading || !ocInfo ? (
              <div className="text-sm text-muted-foreground">Loading...</div>
            ) : (
              <pre className="bg-muted p-4 rounded text-sm overflow-auto max-h-[400px]">
                {JSON.stringify(ocInfo, null, 2)}
              </pre>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center gap-2 text-sm">
        clientVer: {data.clientVer ?? '-'}
      </div>

      <div className="flex items-center gap-2">
        Recreate:
        <div>
          {data.dailyCount?.reCreateCount}/{data.reCreateCount}
        </div>
      </div>
    </div>
  );
};

const ReportCount = ({ data }) => {
  return (
    <div className="flex items-center gap-2">
      Reported:
      {data?.reportCount || 0}
    </div>
  );
};

const ActionSetOffline = ({ data }) => {
  const { trigger, isMutating } = useSWRMutation(
    "/ocs/" + data.id,
    putFetcher,
    {
      onSuccess: () => {},
    },
  );

  const updateState = (e) => {
    e.preventDefault();
    if (isMutating) return;
    const value = data.state === 401 ? 0 : 401;
    trigger({ key: "state", value });
  };

  if (data.state !== 401) {
    return (
      <DropdownMenuItem onClick={updateState} className="text-destructive">
        Set to online
      </DropdownMenuItem>
    );
  }
  return (
    <DropdownMenuItem onClick={updateState} className="text-destructive">
      Set to offline
    </DropdownMenuItem>
  );
};

const ActionCopyId = ({ data }) => {
  return (
    <DropdownMenuItem onSelect={() => navigator.clipboard.writeText(data.id)}>
      <ClipboardIcon className="w-4 h-4 mr-2" />
      Copy ID
    </DropdownMenuItem>
  );
};

const ActionCopyShortId = ({ data }) => {
  return (
    <DropdownMenuItem
      onSelect={() => navigator.clipboard.writeText(data.shortId)}
    >
      <ClipboardIcon className="w-4 h-4 mr-2" />
      Copy Short ID
    </DropdownMenuItem>
  );
};

const ActionCopyImageUrl = ({ data }) => {
  return (
    <DropdownMenuItem
      onSelect={() => navigator.clipboard.writeText(data.avatar)}
    >
      <ClipboardIcon className="w-4 h-4 mr-2" /> Copy Image Url
    </DropdownMenuItem>
  );
};

const AvatarActionDelete = ({ data, mutate }) => {
  const { trigger, isMutating } = useSWRMutation("/ocs", delFetcher);
  const [open, setOpen] = useState(false);

  const handleOpen = (e) => {
    e.preventDefault();
    setOpen(true);
  };

  const handleConfirm = (e) => {
    e.preventDefault();
    if (isMutating) return false;
    trigger(
      { id: data.id },
      {
        onSuccess: () => {
          toast({
            title: "Delete success",
            className: "bg-green-500 text-white",
          });
          setOpen(false);
          mutate();
        },
        onError: (error) => {
          toast({
            variant: "destructive",
            title: "Delete failed",
            description: error?.message || "Delete failed",
          });
        },
      },
    );
  };

  return (
    <>
      <DropdownMenuItem onClick={handleOpen} className="text-destructive">
        Delete it
      </DropdownMenuItem>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Make sure to delete it</DialogTitle>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isMutating}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirm}
              disabled={isMutating}
            >
              {isMutating ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const HeaderText = ({ data }) => {
  const { clientVer, contentLang } = data;
  const { code, short } = useContetLang(contentLang);
  return (
    <>
      {code} - {short} - {clientVer}
    </>
  );
};

export default OcItem;

export const Actions = ({ data, mutate }) => {
  const [open, setOpen] = useState(false);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline">
          Actions
          <ChevronDownIcon className="ml-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-56"
        onInteractOutside={() => setOpen(false)} // 点击外部时关闭菜单
      >
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <ActionCopyId data={data} />
          <ActionCopyShortId data={data} />
          <ActionCopyImageUrl data={data} />
        </DropdownMenuGroup>
        <DropdownMenuLabel className="text-destructive mt-2">
          Please pay attention
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <ActionSetOffline data={data} />
          <AvatarActionDelete data={data} mutate={mutate} />
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const BadgeRow = ({ data }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex items-center gap-2 select-none">
      <div className="flex items-center gap-2">
        <HeartIcon color="red" />
        {data?.likeCount || 0}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <MyLink className="px-0 h-auto inline-flex items-center gap-2">
            <ChatBubbleIcon />
            {data?.commentCount || 0}
          </MyLink>
        </DialogTrigger>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>OC comments list</DialogTitle>
          </DialogHeader>
          <CommentList ocId={data.id} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

const CommentList = ({ ocId }) => {
  const [after, setAfter] = useState(null);
  const [items, setItems] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);

  const { data, isLoading, error } = useSWR([
    `/ocs/${ocId}/comments`,
    { after, limit: 50 },
  ]);

  React.useEffect(() => {
    if (!data) return;
    const list = data?.items || [];
    if (!after) {
      setItems(list);
    } else {
      setItems((prev) => [...prev, ...list]);
    }
  }, [data]);

  if (isLoading && items.length === 0) return <div>Loading...</div>;
  if (error) return <div className="text-destructive">Load failed</div>;
  if (!items.length) return <div>No comments</div>;

  const hasMore = Boolean(data?.nextAfter);

  const loadMore = () => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    setAfter(data?.nextAfter || null);
    setTimeout(() => setLoadingMore(false), 0);
  };

  return (
    <div className="space-y-3 max-h-[60vh] overflow-auto">
      {items.map((c) => (
        <CommentItem key={c.id} c={c} />
      ))}
      {hasMore && (
        <div className="flex justify-center py-2">
          <Button variant="outline" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? "Loading..." : "Load more"}
          </Button>
        </div>
      )}
    </div>
  );
};

const CommentItem = ({ c }) => {
  return (
    <div className="flex items-start gap-3 border rounded p-3">
      <img
        src={cdnUrl(c?.author?.avatar)}
        alt={c?.author?.name}
        className="w-8 h-8 rounded-full object-cover"
      />
      <div className="flex-1 space-y-1">
        <div className="text-sm font-medium">{c?.author?.name}</div>
        {c?.replyToUser && (
          <div className="text-xs text-muted-foreground">
            reply to {c.replyToUser.name}
          </div>
        )}
        {c?.content && (
          <div className="text-sm whitespace-pre-wrap">{c.content}</div>
        )}
        {c?.url && (
          <a
            href={cdnUrl(c.url)}
            target="_blank"
            rel="noreferrer"
            className="block"
          >
            <img
              src={cdnUrl(c.url)}
              alt="comment"
              className="max-h-60 rounded"
            />
          </a>
        )}
        {c?.createTime && (
          <div className="text-xs text-muted-foreground">
            {new Date(c.createTime).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  );
};
